import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp } from 'lucide-react'
import apiClient from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatDateHuman } from '@/lib/utils'
import { useMoney } from './moneyUi'

const SHOW = 6

/**
 * Price changes (F-RPT-N46): stock items that cost noticeably more (or less)
 * this period than before, from every received stock purchase — purchase
 * orders, quick purchases, market runs and approved receipts. Biggest money
 * impact first, so "milk went up" outranks "cardamom went up".
 */
export function PriceChangesCard({ from, to, periodLabel }: { from: string; to: string; periodLabel: string }) {
  const money = useMoney()
  const [all, setAll] = useState(false)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['priceChanges', from, to],
    queryFn: () => apiClient.getPriceChanges({ from, to }),
    staleTime: 5 * 60_000,
  })
  const r = data?.data
  const changes = r?.changes ?? []
  const shown = all ? changes : changes.slice(0, SHOW)

  return (
    <Card>
      <CardHeader className="space-y-1 pb-2">
        <CardTitle className="text-base">Price changes</CardTitle>
        <p className="text-sm text-muted-foreground">
          {periodLabel}. What you paid for stock against the period before
          {r ? ` (${formatDateHuman(`${r.previous_from}T12:00:00`)} – ${formatDateHuman(`${r.previous_to}T12:00:00`)})` : ''}. Moves
          of {r?.threshold_pct ?? 10}% or more.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {isLoading ? (
          <Skeleton className="h-24" />
        ) : isError ? (
          <p className="text-muted-foreground">Price changes couldn't be loaded.</p>
        ) : changes.length === 0 ? (
          <p className="text-muted-foreground">No stock prices moved by 10% or more in this period.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {r && r.extra_cost > 0.005 && (
                <span>
                  Price rises cost <span className="font-medium text-danger-fg tabular-nums">{money.full(r.extra_cost)}</span> more
                </span>
              )}
              {r && r.saved > 0.005 && (
                <span>
                  Price drops saved <span className="font-medium text-success-fg tabular-nums">{money.full(r.saved)}</span>
                </span>
              )}
            </div>
            <ul className="divide-y">
              {shown.map(c => {
                const up = c.change_pct > 0
                const Icon = up ? ArrowUp : ArrowDown
                return (
                  <li key={c.stock_item_id} className="flex items-center gap-3 py-2">
                    <span className={cn('inline-flex w-16 shrink-0 items-center gap-0.5 font-medium tabular-nums', up ? 'text-danger-fg' : 'text-success-fg')}>
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                      {Math.abs(c.change_pct).toFixed(0)}%
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">
                        {c.name}
                        {c.vendor ? <span className="font-normal text-muted-foreground"> · {c.vendor}</span> : null}
                      </div>
                      <div className="truncate text-muted-foreground tabular-nums">
                        {money.full(c.prev_unit_cost)} → {money.full(c.cur_unit_cost)} per {c.unit}
                        {c.baseline === 'last_buy' && c.baseline_date
                          ? ` · against the buy on ${formatDateHuman(`${c.baseline_date}T12:00:00`)}`
                          : ''}
                      </div>
                    </div>
                    <span className="shrink-0 text-right tabular-nums">
                      <span className="block font-medium">
                        {c.extra_cost > 0 ? '+' : '−'}
                        {money.full(Math.abs(c.extra_cost))}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        on {c.cur_qty} {c.unit}
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>
            {changes.length > SHOW && (
              <Button variant="ghost" size="sm" onClick={() => setAll(a => !a)}>
                {all ? 'Show fewer' : `Show all ${changes.length}`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
